import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError } from '../lib/api.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';
import { PageBindings } from './PageBindings.js';
import { GenerationConnectorsPanel } from './GenerationConnectorsPanel.js';

/**
 * AI automation: agents, per-Page bindings, and the human review queue.
 *
 * The review queue is the default view on purpose — it is where a person has
 * work to do, and where a new agent should be watched before it is trusted to
 * publish unattended.
 */
export function AutomationPanel({ initialTab }: { initialTab?: 'studioConnectors' } = {}) {
  const [tab, setTab] = useState<
    'queue' | 'agents' | 'bindings' | 'performance' | 'studioConnectors'
  >(initialTab ?? 'queue');
  const { t } = useI18n();

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('automation.title')}</h2>
        <div className="segmented">
          <button
            type="button"
            className={tab === 'queue' ? 'active' : ''}
            onClick={() => setTab('queue')}
          >
            {t('automation.tab.queue')}
          </button>
          <button
            type="button"
            className={tab === 'agents' ? 'active' : ''}
            onClick={() => setTab('agents')}
          >
            {t('automation.tab.agents')}
          </button>
          <button
            type="button"
            className={tab === 'bindings' ? 'active' : ''}
            onClick={() => setTab('bindings')}
          >
            {t('bindings.tab')}
          </button>
          <button
            type="button"
            className={tab === 'performance' ? 'active' : ''}
            onClick={() => setTab('performance')}
          >
            {t('automation.tab.performance')}
          </button>
          <button
            type="button"
            className={tab === 'studioConnectors' ? 'active' : ''}
            onClick={() => setTab('studioConnectors')}
          >
            {t('automation.tab.studioConnectors')}
          </button>
        </div>
      </div>

      {tab === 'queue' && <ReviewQueue />}
      {tab === 'agents' && <Agents />}
      {tab === 'bindings' && <PageBindings />}
      {tab === 'performance' && <Performance />}
      {tab === 'studioConnectors' && <GenerationConnectorsPanel />}
    </section>
  );
}

function ReviewQueue() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [edits, setEdits] = useState<Record<string, string>>({});

  const queue = useQuery({
    queryKey: ['automation-queue'],
    queryFn: () => api.automationQueue(),
    refetchInterval: 15_000,
  });

  const approve = useMutation({
    mutationFn: ({ id, text }: { id: string; text?: string }) => api.approveReply(id, text),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['automation-queue'] }),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => api.dismissReply(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['automation-queue'] }),
  });

  if (queue.isLoading) return <div className="loading">{t('common.loading')}</div>;

  const items = queue.data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="empty-state">
        <Icon.inbox />
        <p>{t('automation.queueEmpty')}</p>
      </div>
    );
  }

  return (
    <>
      <p className="muted small">{t('automation.queueHint')}</p>

      {items.map((item) => (
        <article key={item.id} className="card">
          <header className="card-head">
            <div>
              <strong>{item.authorName ?? '—'}</strong>{' '}
              <span className="muted small">· {item.pageName}</span>
            </div>
            <span className={`badge ${item.status === 'NEEDS_REVIEW' ? 'warn' : 'accent'}`}>
              {item.status}
            </span>
          </header>

          <p className="text-cell">
            {item.message ?? <span className="muted">{t('common.noText')}</span>}
          </p>

          {item.skipReason && <p className="warn small">{item.skipReason}</p>}

          {item.agentReply ? (
            <>
              <label htmlFor={`reply-${item.id}`}>
                {t('automation.proposedReply')}
                {item.agentConfidence !== null && (
                  <span className="muted">
                    {' '}
                    · {t('automation.confidence')} {item.agentConfidence}
                  </span>
                )}
              </label>
              <textarea
                id={`reply-${item.id}`}
                rows={3}
                value={edits[item.id] ?? item.agentReply}
                onChange={(event) =>
                  setEdits((current) => ({
                    ...current,
                    [item.id]: event.target.value,
                  }))
                }
              />
            </>
          ) : (
            <p className="muted small">{t('automation.noReply')}</p>
          )}

          {approve.isError && (
            <p className="error small">
              {approve.error instanceof ApiError ? approve.error.message : t('publish.failed')}
            </p>
          )}

          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={!item.agentReply || approve.isPending}
              onClick={() => approve.mutate({ id: item.id, text: edits[item.id] })}
            >
              <Icon.check />
              {edits[item.id] && edits[item.id] !== item.agentReply
                ? t('automation.approveEdited')
                : t('automation.approve')}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => dismiss.mutate(item.id)}
              disabled={dismiss.isPending}
            >
              {t('automation.dismiss')}
            </button>
          </div>
        </article>
      ))}
    </>
  );
}

function Agents() {
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const [form, setForm] = useState({
    name: '',
    mode: 'SYNC' as 'SYNC' | 'ASYNC',
    externalAgentId: '',
    authToken: '',
    agentKey: '',
    callbackSecret: '',
  });
  const [error, setError] = useState<string | null>(null);
  /*
   * Which agent the form is editing, or null for a new one.
   *
   * One form for both, because they ask for the same things — and because two
   * would drift, which is how a field ends up correctable in one place and not
   * the other.
   */
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

  const agents = useQuery({ queryKey: ['agents'], queryFn: api.agents });

  const blank = {
    name: '',
    mode: 'SYNC' as 'SYNC' | 'ASYNC',
    externalAgentId: '',
    authToken: '',
    agentKey: '',
    callbackSecret: '',
  };

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      const body = {
        name: form.name,
        mode: form.mode,
        externalAgentId: form.externalAgentId || undefined,
        // Blank means "keep what is stored". A secret is shown once and gone,
        // so correcting a name must never ask for it back.
        authToken: form.authToken || undefined,
        agentKey: form.agentKey || undefined,
        callbackSecret: form.callbackSecret || undefined,
      };
      // Discarded on purpose: create returns an id and update returns an ok,
      // and the caller wants neither — it wants the list refreshed.
      if (editing) await api.updateAgent(editing.id, body);
      else await api.createAgent(body);
    },
    onSuccess: () => {
      setForm(blank);
      setEditing(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('common.requestFailed')),
  });

  const remove = useMutation({
    mutationFn: api.deleteAgent,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agents'] }),
  });

  return (
    <>
      {!agents.data?.automationEnabled && (
        <div className="notice warn">{t('automation.disabledNotice')}</div>
      )}

      {(agents.data?.agents.length ?? 0) > 0 && (
        <div className="grid-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t('automation.agentName')}</th>
                <th>{t('automation.agentMode')}</th>
                <th>{t('automation.agentEndpoint')}</th>
                <th>{t('automation.agentCredential')}</th>
                <th>{t('automation.agentCallbackSecret')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {agents.data?.agents.map((agent) => (
                <tr key={agent.id}>
                  <td>
                    <strong>{agent.name}</strong>
                  </td>
                  <td>
                    <span className="badge accent">{agent.mode}</span>
                  </td>
                  <td className="mono tiny">{agent.externalAgentId ?? '—'}</td>
                  <td className="small">
                    {agent.hasAuthToken ? t('automation.agentStored') : '—'}
                  </td>
                  <td className="small">
                    {agent.hasCallbackSecret ? t('automation.agentStored') : '—'}
                  </td>
                  <td>
                    {/*
                      Edit before delete, and not only in the order of the
                      buttons: without one, fixing a mistyped character meant
                      deleting the agent and building it again — which takes
                      the Page bindings with it.
                    */}
                    <button
                      type="button"
                      className="ghost small"
                      onClick={() => {
                        setEditing({ id: agent.id, name: agent.name });
                        setForm({
                          name: agent.name,
                          mode: agent.mode as 'SYNC' | 'ASYNC',
                          externalAgentId: agent.externalAgentId ?? '',
                          // Left blank on purpose: a stored secret cannot be
                          // read back, and blank means "keep it".
                          authToken: '',
                          agentKey: '',
                          callbackSecret: '',
                        });
                        setError(null);
                      }}
                    >
                      {t('automation.agentEdit')}
                    </button>
                    <button
                      type="button"
                      className="danger small"
                      onClick={() => remove.mutate(agent.id)}
                    >
                      <Icon.trash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card composer">
        <h3>{t('automation.addAgent')}</h3>

        <label htmlFor="agent-name">{t('automation.agentName')}</label>
        <input
          id="agent-name"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />

        <label htmlFor="agent-mode">{t('automation.agentMode')}</label>
        <select
          id="agent-mode"
          value={form.mode}
          onChange={(event) => setForm({ ...form, mode: event.target.value as 'SYNC' | 'ASYNC' })}
        >
          <option value="SYNC">{t('automation.modeSyncLabel')}</option>
          <option value="ASYNC">{t('automation.modeAsyncLabel')}</option>
        </select>
        <p className="muted small">{t('automation.modeHint')}</p>

        {/*
          An identity, not an address.

          This box used to hold the whole URL — host, prefix, route, id and a
          final segment the feedback address was derived from. One typo broke
          both wires, and none of the ways to mistype it produced an error
          anybody could read: a wrong host answered a redirect that was taken
          for an empty reply from the agent. What is never written cannot be
          got wrong, so the road comes from the channel now and only the name
          is asked for.
        */}
        <label htmlFor="agent-id">{t('automation.agentId')}</label>
        <input
          id="agent-id"
          className="ltr"
          placeholder="00000000-0000-0000-0000-000000000000"
          value={form.externalAgentId}
          onChange={(event) => setForm({ ...form, externalAgentId: event.target.value })}
        />
        <p className="muted small">{t('automation.agentIdHint')}</p>

        <label htmlFor="agent-token">{t('automation.bearerToken')}</label>
        <input
          id="agent-token"
          type="password"
          className="ltr"
          value={form.authToken}
          onChange={(event) => setForm({ ...form, authToken: event.target.value })}
        />
        <p className="muted small">{t('automation.bearerHint')}</p>

        <label htmlFor="agent-key">{t('automation.agentKey')}</label>
        <input
          id="agent-key"
          className="ltr"
          value={form.agentKey}
          onChange={(event) => setForm({ ...form, agentKey: event.target.value })}
        />

        {form.mode === 'ASYNC' && (
          <>
            <label htmlFor="agent-secret">{t('automation.callbackSecret')}</label>
            <input
              id="agent-secret"
              type="password"
              className="ltr"
              value={form.callbackSecret}
              onChange={(event) => setForm({ ...form, callbackSecret: event.target.value })}
            />
            <p className="muted small">{t('automation.callbackSecretHint')}</p>
            {agents.data?.callbackUrlTemplate && (
              <p className="muted small">
                {t('automation.callbackUrl')}:{' '}
                <code className="mono tiny">{agents.data.callbackUrlTemplate}</code>
              </p>
            )}
          </>
        )}

        {editing && (
          <p className="muted small">{t('automation.agentEditing', { name: editing.name })}</p>
        )}
        {error && <p className="error small">{error}</p>}

        <button
          type="button"
          className="primary"
          onClick={() => save.mutate()}
          disabled={!form.name || (!editing && !form.externalAgentId) || save.isPending}
        >
          {editing ? t('automation.agentSave') : t('automation.saveAgent')}
        </button>
        {editing && (
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setEditing(null);
              setForm(blank);
              setError(null);
            }}
          >
            {t('automation.agentCancel')}
          </button>
        )}
      </div>
    </>
  );
}

function Performance() {
  const { t } = useI18n();
  const fmt = useFormatters();

  const performance = useQuery({
    queryKey: ['agent-performance'],
    queryFn: () => api.agentPerformance(30),
  });

  if (performance.isLoading) return <div className="loading">{t('common.loading')}</div>;

  const rows = performance.data?.performance ?? [];

  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <Icon.robot />
        <p>{t('automation.perfEmpty')}</p>
      </div>
    );
  }

  return (
    <>
      <p className="muted small">
        {t('automation.perfWindow', {
          days: performance.data?.windowDays ?? 30,
        })}
      </p>

      <div className="grid-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('automation.agentName')}</th>
              <th>{t('automation.agentMode')}</th>
              <th>{t('automation.calls')}</th>
              <th>{t('automation.answered')}</th>
              <th>{t('automation.declined')}</th>
              <th>{t('automation.empty')}</th>
              <th>{t('automation.timedOut')}</th>
              <th>{t('automation.failedCount')}</th>
              <th>{t('automation.successRate')}</th>
              <th>{t('automation.avgMs')}</th>
              <th>{t('automation.p95Ms')}</th>
              <th>{t('automation.avgReply')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.agentConfigId}>
                <td>
                  <strong>{row.name}</strong>
                </td>
                <td>
                  <span className="badge accent">{row.mode}</span>
                </td>
                <td>{fmt.number(row.totalCalls)}</td>
                <td>{fmt.number(row.succeeded)}</td>
                <td>{fmt.number(row.declined)}</td>
                <td className={row.empty > 0 ? 'warn' : ''}>{fmt.number(row.empty)}</td>
                <td className={row.timedOut > 0 ? 'warn' : ''}>{fmt.number(row.timedOut)}</td>
                <td className={row.failed > 0 ? 'error' : ''}>{fmt.number(row.failed)}</td>
                <td>
                  <span className={`badge ${row.successRate >= 0.9 ? 'ok' : 'warn'}`}>
                    {Math.round(row.successRate * 100)}%
                  </span>
                </td>
                <td>{row.avgLatencyMs ?? '—'}</td>
                <td>{row.p95LatencyMs ?? '—'}</td>
                <td>{row.avgReplyChars ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
